import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

interface CardProps {
  icon: string;
  title: string;
  description: string;
  to: string;
  dark?: boolean;
  delay?: number;
  hideLabel?: boolean;
}

function SupportCard({ icon, title, description, to, dark = false, delay = 0, hideLabel = false }: CardProps) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <Link
        to={to}
        className={`group flex flex-col p-8 sm:p-10 h-full cursor-pointer transition-transform duration-200 hover:-translate-y-1 ${
          dark ? 'bg-black text-white' : 'bg-[#F5C518] text-black'
        }`}
      >
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 transition-colors ${
            dark ? 'bg-[#F5C518]' : 'bg-black'
          }`}
        >
          <i className={`${icon} text-xl ${dark ? 'text-black' : 'text-[#F5C518]'}`}></i>
        </div>
        <h3 className="font-black text-xl uppercase tracking-tight mb-3 leading-tight">{title}</h3>
        <p className={`text-sm leading-relaxed flex-1 ${dark ? 'text-gray-300' : 'text-black/70'}`}>{description}</p>
        <div className="mt-6 flex items-center gap-2">
          {!hideLabel && (
            <span className={`text-xs font-black tracking-widest uppercase ${dark ? 'text-[#F5C518]' : 'text-black'}`}>
              Learn more
            </span>
          )}
          <div className="w-4 h-4 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-1">
            <i className={`ri-arrow-right-line text-base ${dark ? 'text-[#F5C518]' : 'text-black'}`}></i>
          </div>
        </div>
      </Link>
    </div>
  );
}

function SectionLabel({ number, title }: { number: string; title: string }) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className={`mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <div className="flex items-end gap-6 mb-4">
        <span className="text-7xl sm:text-8xl font-black text-[#F5C518]/30 leading-none select-none">{number}</span>
        <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tight text-black leading-none pb-2">{title}</h2>
      </div>
      <div className="w-16 h-1 bg-[#F5C518]"></div>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col bg-white font-sans">
      <Navbar />

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 bg-white pt-28">

        {/* Connect with Support */}
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <SectionLabel number="01" title="Connect with Support" />
          <div className="grid md:grid-cols-2 gap-1">
            <SupportCard
              icon="ri-customer-service-2-line"
              title="Contact Support"
              description="Connect directly with our support specialists for immediate assistance with your account and service inquiries."
              to="/contact-support"
              delay={0}
              hideLabel
            />
            <SupportCard
              icon="ri-file-text-line"
              title="Dispute Resolution Form"
              description="Submit formal dispute resolution requests through our structured process for comprehensive issue resolution."
              to="/dispute-resolution"
              dark
              delay={100}
              hideLabel
            />
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="h-px bg-black/10"></div>
        </div>

        {/* Subscription Management */}
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <SectionLabel number="02" title="Subscription Management" />
          <div className="grid md:grid-cols-3 gap-1">
            <SupportCard
              icon="ri-close-circle-line"
              title="Cancelling my subscription"
              description="Learn how to cancel your subscription and understand the cancellation process."
              to="/cancelling-my-subscription"
              delay={0}
            />
            <SupportCard
              icon="ri-refresh-line"
              title="Does my subscription auto-renew?"
              description="Understand how subscription renewals work and how to manage them."
              to="/does-my-subscription-automatically-renew"
              dark
              delay={100}
            />
            <SupportCard
              icon="ri-question-line"
              title="Product or service not received?"
              description="Get help when you haven't received your purchased product or service."
              to="/what-if-I-never-received-the-product-or-service"
              delay={200}
            />
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="h-px bg-black/10"></div>
        </div>

        {/* Billing & Payments */}
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <SectionLabel number="03" title="Billing &amp; Payments" />
          <div className="grid md:grid-cols-3 gap-1">
            <SupportCard
              icon="ri-refund-2-line"
              title="Where is my refund?"
              description="Track your refund request and understand processing timelines."
              to="/where-is-my-refund"
              dark
              delay={0}
            />
            <SupportCard
              icon="ri-bank-card-line"
              title="Unrecognized charge?"
              description="Report and resolve charges you don't recognize on your account."
              to="/I-dont-recognize-this-charge"
              delay={100}
            />
            <SupportCard
              icon="ri-alert-line"
              title="Unaware of the charges"
              description="Get help with charges you weren't aware of or didn't authorize."
              to="/I-was-unaware-of-the-charges"
              dark
              delay={200}
            />
          </div>
        </section>

        {/* CTA strip */}
        <section className="bg-black py-20 px-6 sm:px-10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black uppercase text-white tracking-tight leading-tight">
                Still need help?
              </h2>
              <p className="text-gray-400 text-sm mt-2 tracking-wide">Our team is ready to assist you.</p>
            </div>
            <Link
              to="/contact-support"
              className="bg-[#F5C518] text-black font-black text-xs tracking-widest uppercase px-8 py-4 rounded-full hover:bg-white transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              GET IN TOUCH
            </Link>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
